import { sportsFor } from '@painpal/core';
import { Bike, Link2 } from 'lucide-react';
import { useState } from 'react';
import { useThresholds, useThresholdSuggestions } from '../../api/queries';
import { SportTag } from '../../components/SportTag';
import { formatDayMonthYear } from '../../lib/format';
import { SportSuggestion } from './SportSuggestion';
import { StepChart } from './StepChart';
import { ThresholdHistory, type Editing } from './ThresholdHistory';
import { currentThreshold, hrStripLabels, hrZoneBounds, SPORT_LTHR_COPY, thresholdSegments } from './thresholdModel';
import { ZoneStrip } from './ZoneStrip';
import settings from './settings.module.css';
import styles from './sportSettings.module.css';

const HR = SPORT_LTHR_COPY.other;

function AppliesTo() {
  return (
    <div className={styles.appliesTo}>
      <span className={styles.appliesLabel}>APPLIES TO</span>
      <ul aria-label="APPLIES TO" className={styles.tags}>
        {sportsFor('other').map((sport) => <li key={sport}><SportTag sport={sport} /></li>)}
      </ul>
    </div>
  );
}

function OtherZones({ today }: { today: string }) {
  const current = currentThreshold(useThresholds('lthr', 'other').data?.items ?? [], today);
  if (current === undefined) return null;
  return <ZoneStrip model="hr_lthr" title={`YOUR HR ZONES FROM ${current.value} BPM`} labels={hrStripLabels(hrZoneBounds(current.value))} />;
}

function OtherHrCard({ today }: { today: string }) {
  const [editing, setEditing] = useState<Editing>(null);
  const otherLthr = useThresholdSuggestions().data?.otherLthr;
  return (
    <section className={settings.ftpPanel} aria-labelledby="other-hr-title">
      <header className={settings.ftpHead}>
        <div>
          <h2 id="other-hr-title" className={settings.ftpTitle}>THRESHOLD HR HISTORY</h2>
          <p className={settings.ftpSub}>ONE VALUE FOR SWIM, WALK / HIKE, STRENGTH AND OTHER · EACH ACTIVITY USES THE VALUE VALID ON ITS DATE</p>
        </div>
        <button type="button" className={`btn btn-pink ${settings.addFtp}`} onClick={() => setEditing({ mode: 'new' })}>+ ADD HR</button>
      </header>
      <AppliesTo />
      <SportSuggestion action="other-lthr" suggestion={otherLthr} />
      <ThresholdHistory
        copy={HR} today={today} editing={editing} setEditing={setEditing}
        summary={(items) => items.length > 0 && <StepChart segments={thresholdSegments(items, today, HR)} copy={HR} />}
      />
      <OtherZones today={today} />
      <p className={`sr-only-phone ${styles.cardNote}`}>Swims have no pace-based Load for now: they use HR like everything else here. An activity with no HR gets NO LOAD · NO HR.</p>
    </section>
  );
}

function MaxHrCard({ today, onEditMaxHr }: { today: string; onEditMaxHr: () => void }) {
  const current = currentThreshold(useThresholds('max_hr').data?.items ?? [], today);
  return (
    <section className={settings.card} data-tone="strong" aria-labelledby="other-max-hr-title">
      <h2 id="other-max-hr-title" className={settings.cardTitle}>MAX HR</h2>
      <p className={styles.maxHrValueRow}>
        <span className={settings.hrValue}>{current === undefined ? '—' : `${current.value} BPM`}</span>
        <span className={styles.sharedChip}><Link2 size={10} aria-hidden="true" />SHARED BY EVERY SPORT</span>
      </p>
      {current !== undefined && <span className={settings.hrSince}>{`SINCE ${formatDayMonthYear(current.effectiveFrom)}`}</span>}
      <p className={settings.note}>Max HR is one value for you, not per sport. Edit it, and see suggestions from your rides and runs, under RIDE.</p>
      <button type="button" className={`btn btn-outline ${styles.editUnderRide}`} onClick={onEditMaxHr}><Bike size={14} aria-hidden="true" />EDIT UNDER RIDE ›</button>
    </section>
  );
}

export function OtherThresholds({ today, onEditMaxHr }: { today: string; onEditMaxHr: () => void }) {
  return (
    <>
      <div className={settings.thresholds}><OtherHrCard today={today} /></div>
      <div className={settings.sideTop}><MaxHrCard today={today} onEditMaxHr={onEditMaxHr} /></div>
    </>
  );
}
